import { useParams, useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import StatusBadge from '../components/common/StatusBadge';
import EmptyState from '../components/common/EmptyState';
import { PackageDetailShimmer } from '../components/shimmer';
import { useGetItemByIdQuery } from '../store/edosApi';
import { useListOffersQuery } from '../store/siteContentApi';
import { mapApiToPackageDetail } from '../utils/mapApiDetail';
import { resolvePricing } from '../utils/offerLookup';
import { useCart } from '../context/CartContext';
import { useUI } from '../context/UIContext';
import { ROUTES } from '../constants/routes';

const SectionBand = ({ label, right }) => (
  <Box
    sx={{
      bgcolor: 'var(--forest)',
      p: '10px 18px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
    }}
  >
    <Typography
      sx={{ fontSize: 10, fontWeight: 700, color: 'rgba(255,255,255,0.9)', letterSpacing: '0.18em', textTransform: 'uppercase' }}
    >
      {label}
    </Typography>
    {right && <Typography sx={{ fontSize: 11, color: 'rgba(255,255,255,0.55)', fontWeight: 500 }}>{right}</Typography>}
  </Box>
);

const PackageDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { toggleItem, inCart } = useCart();
  const { showToast } = useUI();

  const { data, isLoading, isError, error } = useGetItemByIdQuery(id, { skip: !id });
  const rawPkg = mapApiToPackageDetail(data);

  // Apply admin-curated offer if one targets this package id.
  const { data: offers = [] } = useListOffersQuery();
  const pricing = rawPkg ? resolvePricing(offers, rawPkg.id, rawPkg.price) : null;
  const pkg = rawPkg && pricing
    ? { ...rawPkg, price: pricing.finalPrice, original: pricing.originalPrice }
    : rawPkg;
  const offerBadge = pricing?.badge || '';

  if (isLoading) {
    return <PackageDetailShimmer />;
  }

  if (isError || !pkg || data?.notFound) {
    return (
      <Container sx={{ py: 10 }}>
        <EmptyState
          icon="📦"
          title="Package not found"
          description={error?.data?.message || 'This package may have been discontinued.'}
          action={
            <Button variant="contained" onClick={() => navigate(ROUTES.PACKAGES)}>
              Browse packages
            </Button>
          }
        />
      </Container>
    );
  }

  const isInCart = inCart(pkg.id, 'pkg');

  const handleCart = () => {
    toggleItem({
      id: pkg.id,
      type: 'pkg',
      name: pkg.name,
      price: pkg.price,
      original: pkg.original,
      icon: pkg.icon,
      tat: pkg.tat,
      paramCount: pkg.paramCount,
    });
    showToast(isInCart ? 'Removed from cart' : 'Added to cart');
  };

  const handleBookNow = () => {
    if (!isInCart) handleCart();
    navigate(ROUTES.CART);
  };

  // Breadcrumb back: prefer actual browser history so a user who landed
  // here from Specials/Home/Cart returns there, not to the packages list.
  // Fall back to the listing only when there's no history (deep link).
  const handleBreadcrumbBack = () => {
    if (window.history.length > 1) navigate(-1);
    else navigate(ROUTES.PACKAGES);
  };

  return (
    <Box sx={{ bgcolor: 'var(--cream)', minHeight: '100vh' }}>
      <Box sx={{ bgcolor: '#fff', borderBottom: '1px solid var(--border-l)', py: 1.75 }}>
        <Container maxWidth="xl" sx={{ maxWidth: 1300, px: { xs: 2, md: 4 } }}>
          <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1, lineHeight: 1 }}>
            <Typography
              component="button"
              onClick={handleBreadcrumbBack}
              sx={{
                fontSize: 13,
                fontWeight: 700,
                color: 'var(--stone)',
                background: 'none',
                border: 0,
                p: 0,
                cursor: 'pointer',
                textDecoration: 'none',
                lineHeight: 1,
                fontFamily: 'inherit',
                '&:hover': { color: 'var(--sage)' },
              }}
            >
              ← Back
            </Typography>
            <Typography component="span" sx={{ fontSize: 13, color: 'var(--border)', lineHeight: 1 }}>
              /
            </Typography>
            <Typography
              component="span"
              sx={{ fontSize: 13, fontWeight: 600, color: 'var(--sage)', lineHeight: 1 }}
            >
              {pkg.name}
            </Typography>
          </Box>
        </Container>
      </Box>

      <Container maxWidth="xl" sx={{ maxWidth: 1300, py: 4.5, px: { xs: 2, md: 4 } }}>
        {/* Hero card */}
        <Box sx={{ bgcolor: 'var(--forest)', color: '#fff', borderRadius: '20px', p: { xs: 3, md: 3.5 }, mb: 2.5 }}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              alignItems: { xs: 'flex-start', md: 'center' },
              gap: { xs: 2, md: 3 },
            }}
          >
            {/* Icon */}
            <Box
              sx={{
                width: 64,
                height: 64,
                bgcolor: 'rgba(255,255,255,0.08)',
                borderRadius: '14px',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 30,
                border: '1px solid rgba(255,255,255,0.12)',
                flexShrink: 0,
                lineHeight: 1,
              }}
            >
              {pkg.icon}
            </Box>

            {/* Title block */}
            <Box sx={{ flex: 1, minWidth: 0 }}>
              {pkg.tag && (
                <Box
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    bgcolor: 'var(--amber)',
                    color: '#fff',
                    fontSize: 10,
                    fontWeight: 800,
                    letterSpacing: '0.12em',
                    textTransform: 'uppercase',
                    borderRadius: 100,
                    px: 1.25,
                    py: 0.5,
                    mb: 1.25,
                    lineHeight: 1,
                  }}
                >
                  {pkg.tag}
                </Box>
              )}
              <Typography
                className="serif"
                sx={{
                  fontSize: { xs: 26, sm: 30, md: 36 },
                  fontWeight: 600,
                  fontStyle: 'italic',
                  color: '#fff',
                  lineHeight: 1.1,
                  mb: 1,
                }}
              >
                {pkg.name}
              </Typography>
              <Box
                sx={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 1,
                  alignItems: 'center',
                }}
              >
                <Typography component="span" sx={{ fontSize: 12, color: 'rgba(255,255,255,0.5)' }}>
                  {pkg.paramCount} parameters
                </Typography>
                <Typography component="span" sx={{ color: 'rgba(255,255,255,0.2)' }}>·</Typography>
                <Typography component="span" sx={{ fontSize: 12, color: 'rgba(255,255,255,0.5)' }}>
                  Report in {pkg.tat}
                </Typography>
                <Typography component="span" sx={{ color: 'rgba(255,255,255,0.2)' }}>·</Typography>
                <Typography component="span" sx={{ fontSize: 12, color: 'rgba(255,255,255,0.5)' }}>
                  {pkg.gender}
                </Typography>
              </Box>
            </Box>

            {/* Price + buttons (right) */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                flexShrink: 0,
                width: { xs: '100%', md: 'auto' },
              }}
            >
              <Box sx={{ textAlign: { xs: 'left', md: 'right' }, flexShrink: 0 }}>
                <Box
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    bgcolor: 'rgba(201,125,8,0.15)',
                    border: '1px solid rgba(201,125,8,0.3)',
                    color: 'var(--amber)',
                    fontSize: 9,
                    fontWeight: 700,
                    letterSpacing: '0.08em',
                    borderRadius: 100,
                    px: 0.875,
                    py: 0.375,
                    mb: 0.625,
                    lineHeight: 1,
                  }}
                >
                  ✦ Women's Day Sale
                </Box>
                <Typography
                  className="serif"
                  sx={{
                    fontSize: { xs: 28, md: 32 },
                    fontWeight: 700,
                    color: '#fff',
                    lineHeight: 1,
                  }}
                >
                  ₹{pkg.price.toLocaleString()}
                </Typography>
                {/* Original + save line only when an admin offer is live. */}
                {pricing && pkg.original > pkg.price && (
                  <>
                    <Typography sx={{ fontSize: 11, textDecoration: 'line-through', color: 'rgba(255,255,255,0.35)', mt: 0.25 }}>
                      ₹{pkg.original.toLocaleString()}
                    </Typography>
                    <Stack direction="row" spacing={0.5} alignItems="center" justifyContent={{ xs: 'flex-start', md: 'flex-end' }} sx={{ mt: 0.25 }}>
                      {offerBadge && (
                        <Typography sx={{
                          fontSize: 9, fontWeight: 800, letterSpacing: '0.04em',
                          bgcolor: 'var(--amber)', color: '#fff',
                          px: 0.875, py: 0.125, borderRadius: 100,
                        }}>
                          {offerBadge}
                        </Typography>
                      )}
                      <Typography sx={{ fontSize: 10, fontWeight: 700, color: 'var(--amber)', letterSpacing: '0.04em' }}>
                        Save ₹{pkg.original - pkg.price}
                      </Typography>
                    </Stack>
                  </>
                )}
              </Box>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, flex: { xs: 1, md: 'initial' } }}>
                <Button
                  onClick={handleBookNow}
                  sx={{
                    bgcolor: 'var(--amber)',
                    color: '#fff',
                    borderRadius: 100,
                    fontSize: 13,
                    fontWeight: 700,
                    textTransform: 'none',
                    px: 2.5,
                    py: 1,
                    whiteSpace: 'nowrap',
                    '&:hover': { bgcolor: '#C77C1D' },
                  }}
                >
                  Book Now
                </Button>
                <Button
                  onClick={handleCart}
                  sx={{
                    bgcolor: 'transparent',
                    color: '#fff',
                    border: '1.5px solid rgba(255,255,255,0.25)',
                    borderRadius: 100,
                    fontSize: 12,
                    fontWeight: 700,
                    textTransform: 'none',
                    px: 2,
                    py: 0.875,
                    whiteSpace: 'nowrap',
                    '&:hover': { borderColor: '#fff', bgcolor: 'rgba(255,255,255,0.06)' },
                  }}
                >
                  {isInCart ? '✓ In Cart' : '+ Add to Cart'}
                </Button>
              </Box>
            </Box>
          </Box>
        </Box>

        {/* Stat cards — 4-col native grid */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' },
            gap: 1.5,
            mb: 2,
            width: '100%',
          }}
        >
          {[
            ['🔬', 'Parameters', `${pkg.paramCount} markers`],
            ['⏱', 'Turnaround', pkg.tat],
            ['🏠', 'Collection', 'Home (free)'],
            ['🍽', 'Fasting', pkg.fasting ? 'Required (8–12 hrs)' : 'Not required'],
          ].map(([ico, l, v]) => (
            <Box
              key={l}
              sx={{
                bgcolor: '#fff',
                border: '1px solid var(--border-l)',
                borderRadius: '14px',
                p: 2.25,
                textAlign: 'center',
              }}
            >
              <Typography sx={{ fontSize: 22, mb: 0.75, lineHeight: 1 }}>{ico}</Typography>
              <Typography
                sx={{
                  fontSize: 10,
                  fontWeight: 700,
                  letterSpacing: '0.12em',
                  color: 'var(--stone)',
                  textTransform: 'uppercase',
                  mb: 0.5,
                  lineHeight: 1,
                }}
              >
                {l}
              </Typography>
              <Typography sx={{ fontSize: 14, fontWeight: 700, color: 'var(--ink)', lineHeight: 1.2 }}>
                {v}
              </Typography>
            </Box>
          ))}
        </Box>

        {/* Body */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
            gap: 2,
            mb: 2,
            width: '100%',
          }}
        >
          <Box>
            <Stack spacing={1.5}>
              <Box sx={{ bgcolor: '#fff', border: '1px solid var(--border-l)', borderRadius: '16px', overflow: 'hidden' }}>
                <SectionBand label="About This Package" />
                <Box sx={{ p: 2.5 }}>
                  <Typography sx={{ fontSize: 13, color: 'var(--charcoal)', lineHeight: 1.8, fontWeight: 300 }}>
                    {pkg.description}
                  </Typography>
                  {pkg.ideal && (
                    <>
                      <Typography
                        sx={{
                          fontSize: 11,
                          fontWeight: 700,
                          letterSpacing: '0.08em',
                          textTransform: 'uppercase',
                          color: 'var(--stone)',
                          mt: 2,
                          mb: 1,
                        }}
                      >
                        Ideal For
                      </Typography>
                      {pkg.ideal.split(' · ').map((tag) => (
                        <StatusBadge key={tag} tone="amber" sx={{ mr: 0.5, mb: 0.5 }}>
                          {tag}
                        </StatusBadge>
                      ))}
                    </>
                  )}
                </Box>
              </Box>
              {pkg.whyNeed && (
                <Box sx={{ bgcolor: '#fff', border: '1px solid var(--border-l)', borderRadius: '16px', overflow: 'hidden' }}>
                  <SectionBand label="Why You Need This" />
                  <Typography
                    sx={{
                      fontSize: 13,
                      color: 'var(--charcoal)',
                      lineHeight: 1.8,
                      p: 2.5,
                      fontWeight: 300,
                    }}
                  >
                    {pkg.whyNeed}
                  </Typography>
                </Box>
              )}
              <Box sx={{ bgcolor: 'var(--mint)', border: '1.5px solid var(--sage-p)', borderRadius: '16px', overflow: 'hidden' }}>
                <Box sx={{ p: 1.5, px: 2.25, borderBottom: '1px solid var(--sage-p)', display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography sx={{ fontSize: 14 }}>🍽</Typography>
                  <Typography sx={{ fontSize: 10, fontWeight: 700, color: 'var(--forest)', letterSpacing: '0.14em', textTransform: 'uppercase' }}>
                    Preparation Instructions
                  </Typography>
                </Box>
                <Typography sx={{ fontSize: 13, color: 'var(--charcoal)', lineHeight: 1.75, p: 2.25, fontWeight: 300 }}>
                  {pkg.prep}
                </Typography>
              </Box>
            </Stack>
          </Box>

          <Box>
            <Box sx={{ bgcolor: '#fff', border: '1px solid var(--border-l)', borderRadius: '16px', overflow: 'hidden' }}>
              <SectionBand label="Tests Included" right={`${pkg.paramCount} parameters`} />
              <Box sx={{ p: 2.5 }}>
                {(pkg.groups || []).map((g) => (
                  <Box key={g.name} sx={{ mb: 2 }}>
                    <Typography
                      sx={{
                        fontSize: 10,
                        fontWeight: 700,
                        letterSpacing: '0.12em',
                        textTransform: 'uppercase',
                        color: 'var(--stone)',
                        mb: 1,
                      }}
                    >
                      {g.name}
                    </Typography>
                    <Stack direction="row" spacing={0.75} sx={{ flexWrap: 'wrap', gap: 0.75 }}>
                      {g.tests.map((t) => (
                        <StatusBadge key={t} tone="stone">
                          {t}
                        </StatusBadge>
                      ))}
                    </Stack>
                  </Box>
                ))}
              </Box>
            </Box>
          </Box>
        </Box>

        {/* FAQs */}
        {pkg.faqs?.length > 0 && (
          <Box sx={{ bgcolor: '#fff', border: '1px solid var(--border-l)', borderRadius: '16px', overflow: 'hidden', mb: 3 }}>
            <SectionBand label="Frequently Asked Questions" />
            {pkg.faqs.map(([q, a], i) => (
              <Accordion
                key={i}
                elevation={0}
                sx={{ '&:before': { display: 'none' }, borderBottom: '1px solid var(--border-l)', '&.Mui-expanded': { margin: 0 } }}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: 'var(--sage)' }} />} sx={{ px: 2.5 }}>
                  <Typography sx={{ fontSize: 14, fontWeight: 600, color: 'var(--ink)' }}>{q}</Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ px: 2.5, pb: 2 }}>
                  <Typography sx={{ fontSize: 13, color: 'var(--charcoal)', lineHeight: 1.75, fontWeight: 300 }}>{a}</Typography>
                </AccordionDetails>
              </Accordion>
            ))}
          </Box>
        )}

        {/* Ready to book CTA band */}
        <Box
          sx={{
            bgcolor: 'var(--forest)',
            color: '#fff',
            borderRadius: '18px',
            px: { xs: 2.5, md: 3.5 },
            py: { xs: 2.5, md: 2.75 },
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: { xs: 'flex-start', sm: 'center' },
            justifyContent: 'space-between',
            gap: 2,
          }}
        >
          <Box>
            <Typography sx={{ fontSize: 16, fontWeight: 700, color: '#fff', mb: 0.5, lineHeight: 1.2 }}>
              Ready to book?
            </Typography>
            <Typography sx={{ fontSize: 12, color: 'rgba(255,255,255,0.5)', lineHeight: 1.4 }}>
              Home collection · Trustian in 60 min · Report in {pkg.tat}
            </Typography>
          </Box>

          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: { xs: 1.5, md: 2 },
              flexWrap: 'wrap',
              width: { xs: '100%', sm: 'auto' },
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
              <Typography
                className="serif"
                sx={{ fontSize: { xs: 24, md: 28 }, fontWeight: 700, color: '#fff', lineHeight: 1 }}
              >
                ₹{pkg.price.toLocaleString()}
              </Typography>
              {pricing && pkg.original > pkg.price && (
                <Typography
                  sx={{
                    fontSize: 12,
                    textDecoration: 'line-through',
                    color: 'rgba(255,255,255,0.4)',
                    lineHeight: 1,
                  }}
                >
                  ₹{pkg.original.toLocaleString()}
                </Typography>
              )}
            </Box>
            <Button
              onClick={handleBookNow}
              sx={{
                bgcolor: 'var(--amber)',
                color: '#fff',
                borderRadius: 100,
                fontSize: 13,
                fontWeight: 700,
                textTransform: 'none',
                px: 2.5,
                py: 1,
                flexShrink: 0,
                '&:hover': { bgcolor: '#C77C1D' },
              }}
            >
              Book Now →
            </Button>
            <Button
              onClick={handleCart}
              sx={{
                bgcolor: 'transparent',
                color: '#fff',
                border: '1.5px solid rgba(255,255,255,0.25)',
                borderRadius: 100,
                fontSize: 13,
                fontWeight: 700,
                textTransform: 'none',
                px: 2,
                py: 1,
                flexShrink: 0,
                '&:hover': { borderColor: '#fff', bgcolor: 'rgba(255,255,255,0.06)' },
              }}
            >
              {isInCart ? '✓ In Cart' : '+ Add to Cart'}
            </Button>
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default PackageDetail;
